import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import ImageUploadBox from '@/components/common/ImageUploadBox';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';
import CityCascader from '@/components/common/CityCascader';
import { useAuth } from '@/hooks/useAuth';
import { useGeoStore } from '@/stores/geoStore';
import { fetchMyResumes, createResume, updateResume } from '@/service/api';

export default function MyResumePage() {
  const { user } = useAuth();
  const { getCityNameById } = useGeoStore();
  const [resume, setResume] = useState<Api.Resume.ResumeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [previewWork, setPreviewWork] = useState<Api.Resume.ResumePortfolioRequest | null>(null);

  // 表单状态直接使用接口定义的 Api.Resume.ResumeRequest
  const [formData, setFormData] = useState<Api.Resume.ResumeRequest>({
    name: '',
    phone: '',
    email: '',
    wechat_id: '',
    expected_monthly_salary: '',
    geo_city_id: undefined,
    self_introduction: '',
    skill_tags: [],
    education: [],
    experience: [],
    portfolio: []
  });

  // 防重复初始化调用的引用标识
  const isFetchingRef = useRef(false);
  const fetchedRef = useRef(false);

  // 初始化获取个人简历数据
  const loadResume = useCallback(async () => {
    if (isFetchingRef.current || fetchedRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    setErrorMsg('');

    try {
      const res = await fetchMyResumes({ page: 1, page_size: 10 });
      const items = res?.items || [];
      if (items.length > 0) {
        setResume(items[0]);
      } else {
        setResume(null);
      }
      fetchedRef.current = true;
    } catch (err) {
      console.error('获取个人简历接口异常:', err);
      setErrorMsg(err instanceof Error ? err.message : '获取简历数据失败');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadResume();
  }, [loadResume]);

  // 打开编辑模式并直接填充接口返回的结构
  const handleStartEdit = () => {
    setErrorMsg('');
    setSuccessMsg('');
    setFormData({
      name: resume?.name || user?.name || '',
      phone: resume?.phone || user?.phone || '',
      email: resume?.email || user?.email || '',
      wechat_id: resume?.wechat_id || '',
      expected_monthly_salary: resume?.expected_monthly_salary || '',
      geo_city_id: resume?.geo_city_id,
      self_introduction: resume?.self_introduction || '',
      skill_tags: resume?.skill_tags ? [...resume.skill_tags] : [],
      education: resume?.education ? JSON.parse(JSON.stringify(resume.education)) : [],
      experience: resume?.experience ? JSON.parse(JSON.stringify(resume.experience)) : [],
      portfolio: resume?.portfolio ? JSON.parse(JSON.stringify(resume.portfolio)) : []
    });
    setTagInput('');
    setIsEditing(true);
  };

  // 添加技能标签
  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    const currentTags = formData.skill_tags || [];
    if (trimmed && !currentTags.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        skill_tags: [...currentTags, trimmed]
      }));
      setTagInput('');
    }
  };

  // 移除技能标签
  const handleRemoveTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      skill_tags: (prev.skill_tags || []).filter((_, i) => i !== index)
    }));
  };

  // 教育经历相关操作
  const handleAddEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [...(prev.education || []), { school: '', degree: '', major: '', start_date: '', end_date: '' }]
    }));
  };

  const handleRemoveEducation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      education: (prev.education || []).filter((_, i) => i !== index)
    }));
  };

  const handleEducationChange = (index: number, field: keyof Api.Resume.ResumeEducationRequest, value: string) => {
    setFormData(prev => {
      const updated = [...(prev.education || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, education: updated };
    });
  };

  // 工作经验相关操作
  const handleAddExperience = () => {
    setFormData(prev => ({
      ...prev,
      experience: [...(prev.experience || []), { organization: '', position: '', description: '', start_date: '', end_date: '' }]
    }));
  };

  const handleRemoveExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      experience: (prev.experience || []).filter((_, i) => i !== index)
    }));
  };

  const handleExperienceChange = (index: number, field: keyof Api.Resume.ResumeExperienceRequest, value: string) => {
    setFormData(prev => {
      const updated = [...(prev.experience || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, experience: updated };
    });
  };

  // 作品集相关操作
  const handleAddPortfolio = () => {
    setFormData(prev => ({
      ...prev,
      portfolio: [...(prev.portfolio || []), { name: '', intro: '', attachments: null }]
    }));
  };

  const handleRemovePortfolio = (index: number) => {
    setFormData(prev => ({
      ...prev,
      portfolio: (prev.portfolio || []).filter((_, i) => i !== index)
    }));
  };

  const handlePortfolioChange = (index: number, field: keyof Api.Resume.ResumePortfolioRequest, value: string) => {
    setFormData(prev => {
      const updated = [...(prev.portfolio || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, portfolio: updated };
    });
  };



  // 提交/保存简历（直接传入 Api.Resume.ResumeRequest）
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      setErrorMsg('请填写姓名');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      let res: Api.Resume.ResumeResponse;
      if (resume?.id) {
        res = await updateResume(resume.id, formData);
      } else {
        res = await createResume(formData);
      }
      setResume(res);
      setIsEditing(false);
      setSuccessMsg('简历保存成功！');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('保存简历接口异常:', err);
      setErrorMsg(err instanceof Error ? err.message : '保存失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AccountLayout
      activePath="/my-resume"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '我的简历' }]}
    >
      {/* 成功 / 错误提示信息 */}
              {successMsg && (
                <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200/60 text-emerald-700 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-lg text-emerald-500"></i>
                  {successMsg}
                </div>
              )}

              {errorMsg && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200/60 text-red-600 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-fill text-lg text-red-500"></i>
                  {errorMsg}
                </div>
              )}

              {/* 加载状态 */}
              {loading ? (
                <div className="glass-card rounded-2xl p-12 text-center">
                  <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-sm text-foreground-500">正在加载简历数据...</p>
                </div>
              ) : isEditing ? (
                /* 编辑表单视图 */
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 基础信息卡片 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <h2 className="font-heading font-bold text-lg text-foreground-950 mb-6 flex items-center gap-2">
                      <i className="ri-user-settings-line text-primary-500"></i>
                      编辑基本信息
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name || ''}
                          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="请输入姓名"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">期望月薪</label>
                        <input
                          type="text"
                          value={formData.expected_monthly_salary || ''}
                          onChange={e => setFormData(prev => ({ ...prev, expected_monthly_salary: e.target.value }))}
                          placeholder="如：15k-25k 或 面议"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">手机号</label>
                        <input
                          type="text"
                          value={formData.phone || ''}
                          onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="请输入手机号"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">电子邮箱</label>
                        <input
                          type="email"
                          value={formData.email || ''}
                          onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="请输入电子邮箱"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">微信号</label>
                        <input
                          type="text"
                          value={formData.wechat_id || ''}
                          onChange={e => setFormData(prev => ({ ...prev, wechat_id: e.target.value }))}
                          placeholder="请输入微信号"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">常驻城市</label>
                        <CityCascader
                          value={formData.geo_city_id}
                          onChange={(cityId) => setFormData(prev => ({ ...prev, geo_city_id: cityId }))}
                          placeholder="请选择所在省份/城市"
                          clearable
                        />
                      </div>
                    </div>
                  </div>

                  {/* 个人简介卡片 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-file-text-line text-accent-500"></i>
                      个人简介
                    </h2>
                    <textarea
                      rows={4}
                      value={formData.self_introduction || ''}
                      onChange={e => setFormData(prev => ({ ...prev, self_introduction: e.target.value }))}
                      placeholder="请详细描述您的从业经历、核心优势及擅长领域..."
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  {/* 技能标签卡片 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-price-tag-3-line text-secondary-500"></i>
                      技能标签
                    </h2>
                    <div className="flex gap-2 mb-4">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                        placeholder="输入技能标签按回车添加"
                        className="flex-1 px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400"
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="px-4 py-2.5 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer"
                      >
                        添加标签
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(formData.skill_tags || []).map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-accent-50 text-accent-700 border border-accent-200/60">
                          {tag}
                          <button type="button" onClick={() => handleRemoveTag(idx)} className="hover:text-red-500 ml-1 cursor-pointer">
                            <i className="ri-close-line"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* 教育背景卡片 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                        <i className="ri-graduation-cap-line text-primary-500"></i>
                        教育背景
                      </h2>
                      <button
                        type="button"
                        onClick={handleAddEducation}
                        className="text-xs px-3 py-1.5 rounded-lg bg-primary-50 text-primary-700 font-medium hover:bg-primary-100 transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-add-line"></i> 添加教育经历
                      </button>
                    </div>

                    <div className="space-y-4">
                      {(formData.education || []).map((edu, idx) => (
                        <div key={idx} className="p-4 rounded-xl border border-background-200 bg-gray-50/50 space-y-3 relative">
                          <button
                            type="button"
                            onClick={() => handleRemoveEducation(idx)}
                            className="absolute top-3 right-3 text-foreground-400 hover:text-red-500 text-sm cursor-pointer"
                            title="删除"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <input
                              type="text"
                              value={edu.school || ''}
                              onChange={e => handleEducationChange(idx, 'school', e.target.value)}
                              placeholder="学校名称"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                            <input
                              type="text"
                              value={edu.major || ''}
                              onChange={e => handleEducationChange(idx, 'major', e.target.value)}
                              placeholder="专业名称"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                            <input
                              type="text"
                              value={edu.degree || ''}
                              onChange={e => handleEducationChange(idx, 'degree', e.target.value)}
                              placeholder="学历 (如：学士/硕士)"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={edu.start_date || ''}
                              onChange={e => handleEducationChange(idx, 'start_date', e.target.value)}
                              placeholder="入学时间 (如: 2018-09)"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                            <input
                              type="text"
                              value={edu.end_date || ''}
                              onChange={e => handleEducationChange(idx, 'end_date', e.target.value)}
                              placeholder="毕业时间 (如: 2022-06)"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 工作经历卡片 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                        <i className="ri-briefcase-line text-secondary-500"></i>
                        工作 / 项目经验
                      </h2>
                      <button
                        type="button"
                        onClick={handleAddExperience}
                        className="text-xs px-3 py-1.5 rounded-lg bg-secondary-50 text-secondary-700 font-medium hover:bg-secondary-100 transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-add-line"></i> 添加经验
                      </button>
                    </div>

                    <div className="space-y-4">
                      {(formData.experience || []).map((exp, idx) => (
                        <div key={idx} className="p-4 rounded-xl border border-background-200 bg-gray-50/50 space-y-3 relative">
                          <button
                            type="button"
                            onClick={() => handleRemoveExperience(idx)}
                            className="absolute top-3 right-3 text-foreground-400 hover:text-red-500 text-sm cursor-pointer"
                            title="删除"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={exp.organization || ''}
                              onChange={e => handleExperienceChange(idx, 'organization', e.target.value)}
                              placeholder="公司/组织名称"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                            <input
                              type="text"
                              value={exp.position || ''}
                              onChange={e => handleExperienceChange(idx, 'position', e.target.value)}
                              placeholder="职位/角色"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={exp.start_date || ''}
                              onChange={e => handleExperienceChange(idx, 'start_date', e.target.value)}
                              placeholder="开始时间 (如: 2021-03)"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                            <input
                              type="text"
                              value={exp.end_date || ''}
                              onChange={e => handleExperienceChange(idx, 'end_date', e.target.value)}
                              placeholder="结束时间 (如: 2024-05 或 至今)"
                              className="px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                            />
                          </div>
                          <textarea
                            rows={3}
                            value={exp.description || ''}
                            onChange={e => handleExperienceChange(idx, 'description', e.target.value)}
                            placeholder="请描述工作内容与主要成果..."
                            className="w-full px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 代表作品卡片 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                        <i className="ri-folder-open-line text-accent-500"></i>
                        代表作品
                      </h2>
                      <button
                        type="button"
                        onClick={handleAddPortfolio}
                        className="text-xs px-3 py-1.5 rounded-lg bg-accent-50 text-accent-700 font-medium hover:bg-accent-100 transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-add-line"></i> 添加作品
                      </button>
                    </div>

                    <div className="space-y-4">
                      {(formData.portfolio || []).map((item, idx) => (
                        <div key={idx} className="p-4 rounded-xl border border-background-200 bg-gray-50/50 space-y-3 relative">
                          <button
                            type="button"
                            onClick={() => handleRemovePortfolio(idx)}
                            className="absolute top-3 right-3 text-foreground-400 hover:text-red-500 text-sm cursor-pointer"
                            title="删除"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                          <input
                            type="text"
                            value={item.name || ''}
                            onChange={e => handlePortfolioChange(idx, 'name', e.target.value)}
                            placeholder="作品名称"
                            className="w-full px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                          />
                          <textarea
                            rows={2}
                            value={item.intro || ''}
                            onChange={e => handlePortfolioChange(idx, 'intro', e.target.value)}
                            placeholder="作品简述与创作背景..."
                            className="w-full px-3 py-2 rounded-lg border border-background-300 bg-white text-xs outline-none"
                          />

                          {/* 作品素材上传区 */}
                          <div className="pt-2 border-t border-background-200/80">
                            <ImageUploadBox
                              label="作品图片/视频素材"
                              scene="PROFILE"
                              accept="image/*,video/*"
                              isVideo={
                                item.attachments?.category === 'PORTFOLIO_VIDEO' ||
                                (item.attachments?.category || '').toUpperCase().includes('VIDEO')
                              }
                              placeholder="点击或拖拽上传作品图片或视频"
                              subPlaceholder="支持 JPG, PNG, WEBP, MP4, MOV 等格式"
                              value={item.attachments?.asset?.access_url || item.attachments?.upload_asset_id || ''}
                              onChange={(assetId, file) => {
                                setFormData(prev => {
                                  const updated = [...(prev.portfolio || [])];
                                  if (!assetId) {
                                    updated[idx] = {
                                      ...updated[idx],
                                      attachments: null
                                    };
                                  } else {
                                    const isVid = file
                                      ? (file.type.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv|flv|m4v)$/i.test(file.name))
                                      : (item.attachments?.category === 'PORTFOLIO_VIDEO' || (item.attachments?.category || '').toUpperCase().includes('VIDEO'));
                                    const cat: Api.Resume.PortfolioCategory = isVid ? 'PORTFOLIO_VIDEO' : 'PORTFOLIO_IMAGE';
                                    const accessUrl = file ? URL.createObjectURL(file) : (item.attachments?.asset?.access_url || '');

                                    updated[idx] = {
                                      ...updated[idx],
                                      attachments: {
                                        upload_asset_id: assetId,
                                        category: cat,
                                        sort_no: idx,
                                        display_name: file?.name || item.name || '作品素材',
                                        asset: {
                                          id: assetId,
                                          access_url: accessUrl,
                                          file_name: file?.name || item.attachments?.asset?.file_name || '',
                                          file_size: file?.size || item.attachments?.asset?.file_size || 0,
                                          file_type: isVid ? 'VIDEO' : 'IMAGE',
                                          mime_type: file?.type || ''
                                        }
                                      }
                                    };
                                  }
                                  return { ...prev, portfolio: updated };
                                });
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 表单按钮 */}
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-3 rounded-xl bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 transition-all disabled:opacity-50 cursor-pointer"
                    >
                      {submitting ? '正在保存...' : '保存简历'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-6 py-3 rounded-xl border border-background-300 text-foreground-600 text-sm font-medium hover:bg-gray-50 transition-all cursor-pointer"
                    >
                      取消
                    </button>
                  </div>
                </form>
              ) : !resume ? (
                /* 无简历空状态 */
                <div className="glass-card rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-user-line text-2xl"></i>
                  </div>
                  <h2 className="font-heading font-bold text-lg text-foreground-950 mb-2">您尚未创建个人简历</h2>
                  <p className="text-xs text-foreground-500 mb-6 max-w-sm mx-auto">
                    完善个人履历与能力介绍，提升在项目合作与招聘匹配中的推荐曝光率
                  </p>
                  <button
                    onClick={handleStartEdit}
                    className="px-6 py-3 rounded-xl bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 transition-all cursor-pointer shadow-sm"
                  >
                    立即创建简历
                  </button>
                </div>
              ) : (
                /* 简历详情展示视图 */
                <div className="space-y-6">
                  {/* 版本与基本概览卡片 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-background-200/70">
                      <div>
                        <div className="flex items-center gap-3">
                          <h1 className="font-heading font-bold text-xl text-foreground-950">
                            {resume.name || user?.name || '个人简历'}
                          </h1>
                          {resume.version_no !== undefined && (
                            <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                              版本 v{resume.version_no}
                            </span>
                          )}
                        </div>
                        {resume.updated_at && (
                          <p className="text-xs text-foreground-400 mt-1">更新时间：{resume.updated_at}</p>
                        )}
                      </div>

                      <button
                        onClick={handleStartEdit}
                        className="px-4 py-2 rounded-lg bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-400 transition-all cursor-pointer self-start sm:self-auto shrink-0"
                      >
                        编辑简历
                      </button>
                    </div>

                    {/* 基础属性网格 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                      <InfoItem label="期望月薪" value={resume.expected_monthly_salary || '未填写'} />
                      <InfoItem label="手机号" value={resume.phone || user?.phone || '未填写'} />
                      <InfoItem label="电子邮箱" value={resume.email || user?.email || '未填写'} />
                      <InfoItem label="微信号" value={resume.wechat_id || '未填写'} />
                      <InfoItem label="常驻城市" value={getCityNameById(resume.geo_city_id, '未填写')} />
                    </div>
                  </div>

                  {/* 个人简介 */}
                  <div className="glass-card rounded-2xl p-6 md:p-8">
                    <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-user-3-line text-accent-500"></i>
                      个人简介
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                      {resume.self_introduction || '暂无个人简介'}
                    </p>
                  </div>

                  {/* 技能标签 */}
                  {resume.skill_tags && resume.skill_tags.length > 0 && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                      <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-price-tag-3-line text-secondary-500"></i>
                        技能标签
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        {resume.skill_tags.map((tag, idx) => (
                          <span key={idx} className="text-sm px-4 py-2 rounded-xl bg-accent-50 text-accent-700 border border-accent-200/60">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 工作 / 项目经验 */}
                  {resume.experience && resume.experience.length > 0 && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                      <h2 className="font-heading font-bold text-lg text-foreground-950 mb-6 flex items-center gap-2">
                        <i className="ri-briefcase-line text-accent-500"></i>
                        工作 / 项目经验
                      </h2>
                      <div className="space-y-6">
                        {resume.experience.map((exp, idx) => (
                          <div key={idx} className="relative pl-5 border-l-2 border-primary-300">
                            <div className="absolute left-0 top-1.5 w-2.5 h-2.5 -translate-x-1/2 rounded-full bg-primary-500"></div>
                            <p className="text-sm font-semibold text-foreground-900">{exp.position || '工作职位'}</p>
                            <p className="text-xs text-foreground-500 mt-0.5">
                              {exp.organization || '机构/公司'}
                              {(exp.start_date || exp.end_date) && ` · ${exp.start_date || ''} ~ ${exp.end_date || ''}`}
                            </p>
                            {exp.description && (
                              <p className="text-xs text-foreground-600 mt-2 leading-relaxed whitespace-pre-line">
                                {exp.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 教育背景 */}
                  {resume.education && resume.education.length > 0 && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                      <h2 className="font-heading font-bold text-lg text-foreground-950 mb-6 flex items-center gap-2">
                        <i className="ri-graduation-cap-line text-primary-500"></i>
                        教育背景
                      </h2>
                      <div className="space-y-4">
                        {resume.education.map((edu, idx) => (
                          <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-gray-50/70">
                            <div className="w-10 h-10 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                              <i className="ri-graduation-cap-line text-lg"></i>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground-950">{edu.school || '学校名称'}</p>
                              <p className="text-xs text-foreground-500 mt-0.5">
                                {[edu.major, edu.degree].filter(Boolean).join(' · ')}
                                {(edu.start_date || edu.end_date) && ` (${edu.start_date || ''} ~ ${edu.end_date || ''})`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 代表作品 */}
                  {resume.portfolio && resume.portfolio.length > 0 && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                      <h2 className="font-heading font-bold text-lg text-foreground-950 mb-6 flex items-center gap-2">
                        <i className="ri-folder-open-line text-secondary-500"></i>
                        代表作品
                        <span className="text-xs font-normal text-foreground-400">({resume.portfolio.length} 部)</span>
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {resume.portfolio.map((item, idx) => {
                          const coverUrl = item.attachments?.asset?.access_url;
                          const isVideo =
                            item.attachments?.category === 'PORTFOLIO_VIDEO' ||
                            (item.attachments?.category || '').toUpperCase().includes('VIDEO') ||
                            /\.(mp4|mov|webm|avi|mkv|flv|m4v)$/i.test(coverUrl || '');
                          return (
                            <div
                              key={idx}
                              onClick={() => setPreviewWork(item)}
                              className="glass-card rounded-xl overflow-hidden group cursor-pointer border border-gray-200/60 hover:border-primary-300 hover:shadow-md transition-all bg-white flex flex-col justify-between"
                            >
                              {/* 封面/媒体预览区 */}
                              <div className="relative aspect-video overflow-hidden bg-gray-100 flex items-center justify-center">
                                {coverUrl ? (
                                  isVideo ? (
                                    <video
                                      src={coverUrl}
                                      muted
                                      playsInline
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none"
                                    />
                                  ) : (
                                    <img
                                      src={coverUrl}
                                      alt={item.name || '代表作品'}
                                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                                      loading="lazy"
                                    />
                                  )
                                ) : (
                                  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 text-gray-400 group-hover:bg-primary-50/50 transition-colors">
                                    <i className={`${isVideo ? 'ri-vidicon-line' : 'ri-image-line'} text-3xl mb-1 text-gray-300 group-hover:text-primary-400 transition-colors`}></i>
                                    <span className="text-[11px] font-medium">{isVideo ? '点击播放视频' : '点击查看作品'}</span>
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="w-11 h-11 rounded-full bg-white/90 shadow-md flex items-center justify-center transform group-hover:scale-110 transition-transform">
                                    <i className={`${isVideo ? 'ri-play-fill text-2xl text-primary-600 ml-0.5' : 'ri-zoom-in-line text-xl text-primary-600'}`}></i>
                                  </div>
                                </div>
                                {isVideo && (
                                  <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded bg-black/60 backdrop-blur-sm text-[10px] text-white font-medium flex items-center gap-1">
                                    <i className="ri-vidicon-line"></i> 视频
                                  </div>
                                )}
                              </div>

                              {/* 作品信息区 */}
                              <div className="p-4 flex-1 flex flex-col justify-between">
                                <div>
                                  <h3 className="font-semibold text-sm text-foreground-950 mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">
                                    {item.name || '未命名作品'}
                                  </h3>
                                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                                    {item.intro || '暂无作品描述'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

      {/* 代表作品预览弹窗 */}
      <PortfolioPreviewModal
        open={Boolean(previewWork)}
        title={previewWork?.name}
        intro={previewWork?.intro}
        isVideo={
          previewWork?.attachments?.category === 'PORTFOLIO_VIDEO' ||
          (previewWork?.attachments?.category || '').toUpperCase().includes('VIDEO') ||
          /\.(mp4|mov|webm|avi|mkv|flv|m4v)$/i.test(previewWork?.attachments?.asset?.access_url || '')
        }
        mediaUrl={previewWork?.attachments?.asset?.access_url}
        onClose={() => setPreviewWork(null)}
      />
    </AccountLayout>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-foreground-400 mb-1">{label}</p>
      <p className="text-sm font-medium text-foreground-900">{value}</p>
    </div>
  );
}